import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControlLabel,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import { ContentCopyOutlined, ExpandMore } from '@mui/icons-material';
import moment from 'moment';
import { ErrorOutline } from '@mui/icons-material';

import {
  FHAccordion,
  FHButton,
  FHConfirmDialog,
  FHInputField,
  FHRadio,
  FHRadioGroup,
  FHSelect,
  FHSmartTextarea,
  FHTooltip,
} from '../../../../base';
import { useTreatmentPlanEditable } from '../../hooks/useTreatmentPlanEditable';
import { capitalizeFirstLetter, validateSchema } from '../../../../../helpers';
import {
  FORTA_BLUE,
  FORTA_WHITE,
  FORTA_ERROR,
  FORTA_GRAY3,
  FORTA_GRAY4,
  FORTA_YELLOW,
} from '../../../../../utils/colors';
import { getTargetTrialSummary } from '../common/utils/general';
import {
  getV2TargetDuplicationData,
  getTargetStagesForGoalType,
  getTargetTooltipMessage,
  validateV2Goal,
} from './utils/general';
import { targetSchema } from '../common/utils/validationSchema';
import TargetStageChange from './targetStages/TargetStageChange';
import TargetProgress from '../common/components/targetProgress';
import {
  BackgroundTypes,
  MAXIMUM_TARGET_INSTRUCTION_LENGTH,
  MINIMUM_REQUIRED_SENTENCE_LENGTH,
  Themes,
} from '../../../../../constants';
import {
  GOAL_DETAILS_FIELD_IDS,
  GoalTypes,
  GOAL_TARGET_NAME_MIN_CHARS,
  GOAL_TARGET_NAME_MAX_CHARS,
  REASON_FOR_DEACTIVATE_MAX_CHARS,
  EventDurationMeasurementUnit,
  iconButtonStyle,
  TargetStages,
  TreatmentPlanResourceVersion,
} from '../common/constants/general';
import { targetDescriptionBlacklist } from '../../../../../pages/constants';
import TrashIcon from '../../../../svgs/TrashIcon';
import EditOutlinedIcon from '../../../../svgs/EditOutlinedIcon';
import UnsavedIndicator from '../../components/unsavedIndicator';
import StatusTransitionIcon from './components/statusTransitionIcon';
import { useInitialValue } from './hooks';

import type { RootState } from '../../../../../store';
import type { ITreatmentPlanGoal, ITreatmentPlanGoalTarget } from '../../../../../utils/types';
import type { ChangeEvent } from 'react';

function isTargetStage(value: string | number): value is TargetStages {
  return Object.values(TargetStages).some((stage) => stage === value);
}

function isEventDurationMeasurementUnit(
  value: string,
): value is EventDurationMeasurementUnit {
  return Object.values(EventDurationMeasurementUnit).some((unit) => unit === value);
}

interface GoalTargetItemProps {
  index: number;
  goalId: number;
  goal: ITreatmentPlanGoal;
  target: ITreatmentPlanGoalTarget;
  saving: boolean;
  goalType: GoalTypes;
  goalActive: boolean;
  onSave: (target: ITreatmentPlanGoalTarget) => void;
  onDelete: () => void;
  onUpdateField: (key: string, value: any) => void;
  onAddTargetWithValues: (data: any) => void;
  onUpdateTargetActivation: (reason: string) => void;
  onCancelTargetChange: (target: ITreatmentPlanGoalTarget | null) => void;
}

function GoalTargetItem(props: GoalTargetItemProps) {
  const {
    index,
    goalId,
    goal,
    target,
    saving,
    goalType,
    goalActive,
    onSave,
    onDelete,
    onUpdateField,
    onAddTargetWithValues,
    onUpdateTargetActivation,
    onCancelTargetChange,
  } = props;
  const { tpGoalsTrialSummary } = useSelector((state: RootState) => state.goals);
  const isTpEditable = useTreatmentPlanEditable();
  const [isEditing, setIsEditing] = useState<boolean>(true);
  const [activeStatus, setActiveStatus] = useState<boolean>(true);
  const [label, setLabel] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [instruction, setInstruction] = useState<string>('');
  const [stageAtCreation, setStageAtCreation] = useState<TargetStages | null>(null);
  const [isEventDurationRequired, setIsEventDurationRequired] = useState<boolean>(false);
  const [eventDurationMeasurementUnit, setEventDurationMeasurementUnit] =
    useState<EventDurationMeasurementUnit | null>(null);
  const [reason, setReason] = useState<string>('');
  const [isTargetValid, setIsTargetValid] = useState<boolean>(false);
  const [errors, setErrors] = useState<any>(null);

  const [invalidGoalFields, setInvalidGoalFields] = useState<string[]>([]);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [showGoalValidation, setShowGoalValidation] = useState<boolean>(false);
  const [prevTarget, setPrevTarget] = useState<ITreatmentPlanGoalTarget | null>(null);
  const currentStage = target?.stage_change_logs?.[target.stage_change_logs.length - 1]?.new_stage;
  const currentStageDate =
    target?.stage_change_logs?.[target.stage_change_logs.length - 1]?.date_stage_changed;
  const targetTrialSummary = getTargetTrialSummary(tpGoalsTrialSummary, goalId, target?.id ?? 0);
  const initialIsActive = useInitialValue(target?.is_active ?? true, target?.id);

  useEffect(() => {
    if (!target) return;

    setLabel(target.label ?? '');
    setActiveStatus(target.is_active ?? true);
    setDescription(target.description ?? '');
    setInstruction(target.instruction ?? '');
    setStageAtCreation(currentStage ?? stageAtCreation);
    setIsEventDurationRequired(target.is_event_duration_required ?? false);
    setEventDurationMeasurementUnit(target.event_duration_measurement_unit ?? null);
    setIsEditing(target.is_editing ?? false);

    // Create complete data object for validation
    const data = {
      label: target?.label ?? '',
      instruction: target?.instruction ?? '',
      description: target?.description ?? '',
      stage: currentStage ?? stageAtCreation,
      target_version: TreatmentPlanResourceVersion.V2,
    };

    // Validate only the changed field
    const newErrors: { [key: string]: boolean } = {};
    Object.keys(data).forEach((key) => {
      const isValidField = validateSchema(targetSchema, key, data);
      newErrors[key] = !isValidField;
    });
    setErrors(newErrors);
  }, [target]);

  useEffect(() => {
    if (!errors) {
      setIsTargetValid(false);
      return;
    }

    const isValid = !Object.keys(errors).some((key) => errors[key]);
    setIsTargetValid(isValid);
  }, [errors]);

  const handleUpdateField = (key: string, value: string | TargetStages, isBlurred: boolean) => {
    if (isBlurred) {
      onUpdateField(key, value);
    } else {
      if (key === 'label') setLabel(value);
      if (key === 'instruction') setInstruction(value);
      if (key === 'description') setDescription(value);
      if (key === 'stage' && isTargetStage(value)) setStageAtCreation(value);
    }

    // Create complete data object for validation
    const data = {
      label: key === 'label' ? value : target?.label,
      instruction: key === 'instruction' ? value : target?.instruction,
      description: key === 'description' ? value : target?.description,
      stage: key === 'stage' ? value : stageAtCreation,
      target_version: TreatmentPlanResourceVersion.V2,
    };

    // Validate only the changed field
    const newErrors = { ...errors };
    const isValidField = validateSchema(targetSchema, key, data);
    newErrors[key] = !isValidField;
    setErrors(newErrors);
  };

  const handleCancel = () => {
    setShowConfirmation(false);
    setIsEditing(false);
    if (prevTarget) {
      const updatedTarget = {
        ...prevTarget,
        is_active: target?.is_active ?? Boolean(prevTarget.is_active),
        stage_change_logs: target?.stage_change_logs || prevTarget.stage_change_logs,
        stage:
          currentStage ||
          prevTarget.stage_change_logs?.[prevTarget.stage_change_logs.length - 1]?.new_stage,
      };
      onCancelTargetChange(updatedTarget);
    } else {
      onCancelTargetChange(null);
    }
  };

  const validateTarget = (showAlerts = false) => {
    if (label?.length < GOAL_TARGET_NAME_MIN_CHARS) {
      if (showAlerts) {
        toast.error(`Target name requires at least ${GOAL_TARGET_NAME_MIN_CHARS} characters.`);
      }
      return;
    }

    if (label?.length > GOAL_TARGET_NAME_MAX_CHARS) {
      if (showAlerts) {
        toast.error(`Target name requires maximum ${GOAL_TARGET_NAME_MAX_CHARS} characters.`);
      }
      return;
    }

    if (description?.length < MINIMUM_REQUIRED_SENTENCE_LENGTH) {
      if (showAlerts) {
        toast.error(
          `Target description requires at least ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters.`,
        );
      }
      return;
    }

    if (description?.length > MAXIMUM_TARGET_INSTRUCTION_LENGTH) {
      if (showAlerts) {
        toast.error(
          `Target description has more than ${MAXIMUM_TARGET_INSTRUCTION_LENGTH} characters.`,
        );
      }
      return;
    }

    if (instruction?.length < MINIMUM_REQUIRED_SENTENCE_LENGTH) {
      if (showAlerts) {
        toast.error(
          `Target instruction requires at least ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters.`,
        );
      }
      return;
    }

    if (instruction?.length > MAXIMUM_TARGET_INSTRUCTION_LENGTH) {
      if (showAlerts) {
        toast.error(
          `Target instruction has more than ${MAXIMUM_TARGET_INSTRUCTION_LENGTH} characters.`,
        );
      }
      return;
    }
    if (!target?.id && !target?.stage) {
      if (showAlerts) {
        toast.error('Stage at creation is required');
      }
      return;
    }

    return true;
  };

  const handleSaveTarget = () => {
    const isValid = validateTarget(true);
    if (!isValid) return;

    let targetData = {
      ...target,
      instruction,
      label,
      description,
      stage: stageAtCreation ?? target?.stage,
      reason,
      is_editing: false,
    };
    if (goalType === GoalTypes.BehaviorReduction) {
      targetData = {
        ...targetData,
        is_event_duration_required: isEventDurationRequired,
        event_duration_measurement_unit: eventDurationMeasurementUnit,
      };
    }
    onSave(targetData);
  };

  const handleEditingClick = () => {
    const errors = validateV2Goal(goal);
    const filteredErrors = Object.entries(errors).filter(([key]) => key !== 'targets');
    // List invalid fields for validation dialog
    const invalidFields = filteredErrors.flatMap(([key, value]) => {
      if (key === 'measurement') {
        return value ? ['Mastering criteria'] : [];
      }
      return value ? [key.replace(/_/g, ' ')] : [];
    });

    if (invalidFields.length > 0) {
      setInvalidGoalFields(invalidFields);
      setShowGoalValidation(true);
      return;
    }
    if (goal.is_editing) {
      toast.error(
        `Please save the goal '${goal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.label}' before editing the target`,
      );
      return;
    }

    onUpdateField('is_editing', !isEditing);
    if (!isEditing) {
      setPrevTarget(target);
    }
  };

  const selectableStages = getTargetStagesForGoalType(goalType).filter(
    (stage) =>
      stage.value !== TargetStages.OnHold &&
      stage.value !== TargetStages.Resume &&
      stage.value !== TargetStages.Discontinued &&
      stage.value !== TargetStages.Mastered,
  );

  const isValid = validateTarget();

  const hasUnsavedChanges = target?.dirty && target?.is_editing;

  return (
    <Box
      sx={{
        px: 2,
        borderBottom: `2px solid ${FORTA_BLUE.light3}`,
        '&:last-of-type': {
          borderBottom: 'none',
        },
      }}
      data-testid={`targetContainer_${target.label}`}
    >
      <FHAccordion
        colorTheme={Themes.Primary}
        defaultExpanded={false}
        expanded={process.env.VITE_TEST === 'true' ? true : Boolean(target?.expanded)}
        hasErrors={!isValid}
        hasWarnings={hasUnsavedChanges}
      >
        <Box sx={{ position: 'relative' }}>
          {hasUnsavedChanges && <UnsavedIndicator right={'-30px'} />}
          <AccordionSummary
            expandIcon={<ExpandMore data-testid={`expand_target_${index}`} />}
            sx={{
              padding: '16px',
              '&.Mui-focusVisible': {
                backgroundColor: 'transparent',
              },
              '& .MuiAccordionSummary-expandIconWrapper': {
                position: 'absolute',
                right: 10,
                top: 24,
              },
            }}
            onClick={() => onUpdateField('expanded', !target?.expanded)}
          >
            <Grid container>
              <Grid item xs={8} sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography
                  textAlign={'left'}
                  variant="h4"
                  fontWeight={'bold'}
                  mr={isEditing ? 0 : 2}
                  color={FORTA_BLUE.primary}
                >
                  {`${index + 1}: ${capitalizeFirstLetter(label ?? '')}`}
                </Typography>
                {target?.id && (
                  <Box sx={{ display: 'flex', gap: 3, ml: isEditing ? 2 : 0 }}>
                    <StatusTransitionIcon
                      currentIsActive={target.is_active}
                      originalIsActive={initialIsActive}
                      size={30}
                      variant="standard"
                      tooltip={
                        <Typography>
                          {getTargetTooltipMessage(target)
                            .split('<strong>')
                            .map((part, idx) => {
                              if (idx === 0) return part;
                              const [boldText, rest] = part.split('</strong>');
                              return (
                                <>
                                  <strong key={idx}>{boldText}</strong>
                                  {rest}
                                </>
                              );
                            })}
                        </Typography>
                      }
                    />
                  </Box>
                )}
                {isEditing && (
                  <Typography
                    ml={2}
                    textAlign={'left'}
                    alignSelf="center"
                    fontWeight={'700'}
                    lineHeight={'21px'}
                    sx={{
                      color: isTargetValid ? FORTA_BLUE.light1 : FORTA_ERROR,
                    }}
                  >
                    Attention: All fields with * are required to be filled
                  </Typography>
                )}
              </Grid>
              <Grid item xs={4} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                {!isEditing && (
                  <>
                    <FHTooltip
                      tooltipBorderRadius="8px"
                      tooltipBoxShadow="0px 4px 4px 0px rgba(0, 0, 0, 0.25)"
                      title={<Typography>Delete target</Typography>}
                    >
                      <Box component="span" sx={{ display: 'inline-flex' }}>
                        <IconButton
                          data-testid={`deleteTarget_${target?.label}`}
                          disabled={saving}
                          hidden={!isTpEditable}
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete();
                          }}
                          sx={iconButtonStyle}
                        >
                          <TrashIcon color={FORTA_BLUE.primary} />
                        </IconButton>
                      </Box>
                    </FHTooltip>
                    <FHTooltip
                      tooltipBorderRadius="8px"
                      tooltipBoxShadow="0px 4px 4px 0px rgba(0, 0, 0, 0.25)"
                      title={<Typography>Edit target</Typography>}
                    >
                      <Box component="span" sx={{ display: 'inline-flex' }}>
                        <IconButton
                          data-testid={`editTarget_${target?.label}`}
                          sx={iconButtonStyle}
                          hidden={!isTpEditable}
                          disabled={saving || !isTpEditable}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditingClick();
                          }}
                        >
                          <EditOutlinedIcon color={FORTA_BLUE.primary} />
                        </IconButton>
                      </Box>
                    </FHTooltip>
                    {isTpEditable && target?.id && (
                      <FHTooltip
                        tooltipBorderRadius="8px"
                        tooltipBoxShadow="0px 4px 4px 0px rgba(0, 0, 0, 0.25)"
                        title={<Typography>Duplicate target</Typography>}
                      >
                        <Box component="span" sx={{ display: 'inline-flex' }}>
                          <IconButton
                            data-testid={`editTarget_${target?.label}`}
                            sx={iconButtonStyle}
                            hidden={!isTpEditable}
                            disabled={saving || !isTpEditable}
                            onClick={(e) => {
                              e.stopPropagation();
                              onAddTargetWithValues(getV2TargetDuplicationData(target));
                            }}
                          >
                            <ContentCopyOutlined sx={{ color: FORTA_BLUE.primary }} />
                          </IconButton>
                        </Box>
                      </FHTooltip>
                    )}
                  </>
                )}
              </Grid>
            </Grid>
          </AccordionSummary>
        </Box>
        {target?.expanded ? (
          <>
            <AccordionDetails sx={{ p: 3 }}>
              <Grid container>
                {isEditing && (
                  <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <FHButton
                      data-testid="targetsCancelButtonTop"
                      sx={{
                        backgroundColor: FORTA_WHITE,
                        border: `1px solid ${FORTA_BLUE.light3}`,
                        color: FORTA_GRAY4,
                        mr: 3,
                        '&:hover': {
                          backgroundColor: FORTA_WHITE,
                          border: `1px solid ${FORTA_BLUE.light3}`,
                          color: FORTA_GRAY3,
                        },
                        '&.Mui-disabled': {
                          backgroundColor: FORTA_WHITE,
                          color: FORTA_GRAY4,
                        },
                      }}
                      disabled={!isTpEditable}
                      onClick={() => {
                        setShowConfirmation(true);
                      }}
                    >
                      Cancel
                    </FHButton>
                    <Box sx={{ position: 'relative' }}>
                      {target?.dirty && <UnsavedIndicator right="-20px" top="-15px" />}
                      <FHButton
                        data-testid={`targetSaveButtonTop`}
                        colorTheme={Themes.Primary}
                        onClick={handleSaveTarget}
                      >
                        SAVE TARGET
                      </FHButton>
                    </Box>
                  </Grid>
                )}
                {isEditing && (
                  <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', mb: 1 }}>
                    <Typography fontWeight={'bold'} textAlign={'left'} mb={1}>
                      Target name*
                    </Typography>
                    <FHInputField
                      data-testid="targetName"
                      inputHeight={40}
                      colorTheme={Themes.Primary}
                      coloredBackground
                      disabled={saving || !isTpEditable}
                      value={capitalizeFirstLetter(label ?? '')}
                      placeholder="Enter target name"
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        const value = e.target.value;
                        const isPasteAction = value.length - label.length > 2;
                        if (isPasteAction) {
                          setTimeout(() => {
                            handleUpdateField('label', value, false);
                          }, 200);
                        } else {
                          handleUpdateField('label', value, false);
                        }
                      }}
                      onBlur={() => handleUpdateField('label', label, true)}
                      error={errors && errors['label']}
                      helperText={
                        errors && errors['label']
                          ? `${label?.length}/${GOAL_TARGET_NAME_MAX_CHARS}`
                          : ''
                      }
                    />
                  </Grid>
                )}
                <Grid
                  item
                  xs={12}
                  sx={{ display: 'flex', flexDirection: 'column', mt: isEditing ? 2 : 0 }}
                  mb={2}
                >
                  <Box display={'flex'}>
                    <Typography fontWeight={'bold'} textAlign={'left'} mb={0.5}>
                      Target description*
                    </Typography>
                  </Box>
                  {isEditing ? (
                    <FHSmartTextarea
                      id={`tp-target-smartbox-${goalId}-${target.id}-${index}`}
                      data-testid="targetDescription"
                      sx={{ flex: 1 }}
                      fullWidth
                      coloredBackground
                      hightlightOnDetection
                      heightlightColour={FORTA_YELLOW}
                      blackListedWordsPhrases={targetDescriptionBlacklist}
                      disabled={saving || !isTpEditable}
                      placeholder="Describe the target description"
                      value={description}
                      onEdit={(e: ChangeEvent<HTMLTextAreaElement>) => {
                        const value = e.target.value;
                        const isPasteAction = value.length - description.length > 2;
                        if (isPasteAction) {
                          setTimeout(() => {
                            handleUpdateField('description', value, false);
                          }, 200);
                        } else {
                          handleUpdateField('description', value, false);
                        }
                      }}
                      onBlur={() => handleUpdateField('description', description, true)}
                      onCloseExpandDialog={() => {
                        handleUpdateField('description', description, true);
                      }}
                      error={errors && errors['description']}
                      helperText={
                        errors && errors['description']
                          ? description.trim().length > MAXIMUM_TARGET_INSTRUCTION_LENGTH
                            ? `Maximum ${MAXIMUM_TARGET_INSTRUCTION_LENGTH} characters`
                            : `Minimum ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters`
                          : ''
                      }
                    />
                  ) : target?.description === '' || target?.description === null ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <ErrorOutline sx={{ color: FORTA_ERROR }} />
                      <Typography color={FORTA_ERROR} align="left">
                        Not available - Target description is required
                      </Typography>
                    </Box>
                  ) : (
                    <Typography textAlign={'left'} sx={{ wordBreak: 'break-word' }}>
                      {description}
                    </Typography>
                  )}
                </Grid>
                <Grid
                  item
                  xs={12}
                  sx={{ display: 'flex', flexDirection: 'column', mt: isEditing ? 2 : 0 }}
                  mb={2}
                >
                  <Box display={'flex'}>
                    <Typography fontWeight={'bold'} textAlign={'left'} mb={0.5}>
                      Target instructions*
                    </Typography>
                    {isEditing && (
                      <Typography textAlign={'left'} ml={0.5}>
                        (include prompting, environment, and materials)
                      </Typography>
                    )}
                  </Box>
                  {isEditing ? (
                    <FHSmartTextarea
                      id={`tp-target-isntructions-${goalId}-${target.id}-${index}`}
                      data-testid={`targetInstructions`}
                      sx={{ flex: 1 }}
                      fullWidth
                      colorTheme={Themes.Primary}
                      coloredBackground
                      hightlightOnDetection
                      heightlightColour={FORTA_YELLOW}
                      blackListedWordsPhrases={targetDescriptionBlacklist}
                      disabled={saving || !isTpEditable}
                      placeholder="Describe the target instructions"
                      value={instruction}
                      onEdit={(e: ChangeEvent<HTMLTextAreaElement>) => {
                        const value = e.target.value;
                        const isPasteAction = value.length - instruction.length > 2;
                        if (isPasteAction) {
                          setTimeout(() => {
                            handleUpdateField('instruction', value, false);
                          }, 200);
                        } else {
                          handleUpdateField('instruction', value, false);
                        }
                      }}
                      onBlur={() => handleUpdateField('instruction', instruction, true)}
                      onCloseExpandDialog={() => {
                        handleUpdateField('instruction', instruction, true);
                      }}
                      error={errors && errors['instruction']}
                      helperText={
                        errors && errors['instruction']
                          ? instruction.trim().length > MAXIMUM_TARGET_INSTRUCTION_LENGTH
                            ? `Maximum ${MAXIMUM_TARGET_INSTRUCTION_LENGTH} characters`
                            : `Minimum ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters`
                          : ''
                      }
                    />
                  ) : (
                    <Typography textAlign={'left'} sx={{ wordBreak: 'break-word' }}>
                      {instruction}
                    </Typography>
                  )}
                </Grid>
                {goalType === GoalTypes.BehaviorReduction && isEventDurationRequired && (
                  <Grid item xs={4} mt={2} mb={2}>
                    <Typography fontWeight={'bold'} textAlign={'left'} mb={0.5}>
                      Capture the duration of the event?*
                    </Typography>
                    {isEditing ? (
                      <FHRadioGroup
                        data-testid="isEventDurationRequiredRadioGroup"
                        rowGap="16px"
                        name="capture-the-duration-of-the-event"
                        colorTheme={Themes.Primary}
                        coloredBackground
                        value={isEventDurationRequired ? 'Yes' : 'No'}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          // To keep the text field data on blur
                          setTimeout(() => {
                            onUpdateField(
                              'is_event_duration_required',
                              e.target.value === 'Yes' ? true : false,
                            );
                          });
                        }}
                      >
                        <FormControlLabel
                          data-testid="isEventDurationRequiredRadioYesChoice"
                          sx={{ width: 'fit-content' }}
                          value="Yes"
                          control={<FHRadio colorTheme={Themes.Primary} />}
                          label="Yes"
                          disabled={true}
                        />
                        <FormControlLabel
                          data-testid="isEventDurationRequiredRadioNoChoice"
                          sx={{ width: 'fit-content' }}
                          value="No"
                          control={<FHRadio colorTheme={Themes.Primary} />}
                          label="No"
                          disabled={true}
                        />
                      </FHRadioGroup>
                    ) : (
                      <Typography textAlign={'left'}>
                        {isEventDurationRequired ? 'Yes' : 'No'}
                      </Typography>
                    )}
                  </Grid>
                )}
                {goalType === GoalTypes.BehaviorReduction && isEventDurationRequired && (
                  <Grid item xs={4} mt={2} mb={2}>
                    <Typography fontWeight={'bold'} textAlign={'left'} mb={0.5}>
                      Unit of duration for target*
                    </Typography>
                    {isEditing ? (
                      <FHRadioGroup
                        data-testid="targetUnitsRadioGroup"
                        rowGap="16px"
                        colorTheme={Themes.Primary}
                        coloredBackground
                        name="unit-of-duration-for-target"
                        value={eventDurationMeasurementUnit}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          if (!isEventDurationMeasurementUnit(e.target.value)) return;
                          onUpdateField('event_duration_measurement_unit', e.target.value);
                        }}
                      >
                        <FormControlLabel
                          data-testid="targetUnitSeconds"
                          sx={{ width: 'fit-content' }}
                          disabled={true}
                          value={EventDurationMeasurementUnit.Second}
                          control={<FHRadio colorTheme={Themes.Primary} />}
                          label="Seconds"
                        />
                        <FormControlLabel
                          data-testid="targetUnitMinuts"
                          sx={{ width: 'fit-content' }}
                          disabled={true}
                          value={EventDurationMeasurementUnit.Minute}
                          control={<FHRadio colorTheme={Themes.Primary} />}
                          label="Minutes"
                        />
                        <FormControlLabel
                          data-testid="targetUnitHours"
                          sx={{ width: 'fit-content' }}
                          disabled={true}
                          value={EventDurationMeasurementUnit.Hour}
                          control={<FHRadio colorTheme={Themes.Primary} />}
                          label="Hours"
                        />
                      </FHRadioGroup>
                    ) : (
                      <Typography textAlign={'left'}>
                        {eventDurationMeasurementUnit
                          ?.charAt(0)
                          .toUpperCase()
                          .concat(eventDurationMeasurementUnit?.slice(1).toLowerCase()) + 's'}
                      </Typography>
                    )}
                  </Grid>
                )}
                <Grid item xs={6} mt={2}>
                  <Box display="flex" flexDirection="column">
                    <Typography fontWeight="bold" textAlign="left" mb={1}>
                      {target?.id ? 'Current stage*' : 'Stage at creation*'}
                    </Typography>

                    {!target?.id && isEditing ? (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <FHSelect
                          inputHeight={40}
                          data-testid="currentStageSelect"
                          colorTheme={Themes.Primary}
                          backgroundType={BackgroundTypes.Colored}
                          fullWidth
                          sx={{ display: 'flex', maxWidth: 250 }}
                          options={selectableStages}
                          value={stageAtCreation ?? target?.stage}
                          disabled={!isTpEditable}
                          onUpdate={(value) => {
                            if (!isTargetStage(value)) return;
                            setStageAtCreation(value);
                            handleUpdateField('stage', value, true);
                          }}
                          onBlur={() => handleUpdateField('stage', stageAtCreation ?? '', true)}
                          error={!(stageAtCreation ?? target?.stage)}
                        />
                        <Typography sx={{ width: '200px', ml: 2 }} textAlign="left">
                          {` (on ${moment().format('MM/DD/YY')})`}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography textAlign="left">
                        {target?.id
                          ? currentStage
                            ? `${currentStage}${
                                currentStageDate
                                  ? ` (on ${moment(currentStageDate).format('MM/DD/YY')})`
                                  : ''
                              }`
                            : 'No stage available'
                          : target?.stage}
                      </Typography>
                    )}
                  </Box>
                </Grid>
                {target?.id && (
                  <>
                    <TargetStageChange
                      target={target}
                      goalId={goalId}
                      goalType={goalType}
                      goalActive={goalActive}
                    />
                    <TargetProgress
                      goalId={goalId}
                      target={target}
                      trialSummary={targetTrialSummary}
                      onUpdateField={onUpdateField}
                    />
                  </>
                )}
                {isEditing && (
                  <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }} mt={4}>
                    <FHButton
                      data-testid="targetsCancelButton"
                      sx={{
                        backgroundColor: FORTA_WHITE,
                        border: `1px solid ${FORTA_BLUE.light3}`,
                        color: FORTA_GRAY4,
                        mr: 3,
                        '&:hover': {
                          backgroundColor: FORTA_WHITE,
                          border: `1px solid ${FORTA_BLUE.light3}`,
                          color: FORTA_GRAY3,
                        },
                        '&.Mui-disabled': {
                          backgroundColor: FORTA_WHITE,
                          color: FORTA_GRAY4,
                        },
                      }}
                      disabled={!isTpEditable}
                      onClick={() => {
                        setShowConfirmation(true);
                      }}
                    >
                      Cancel
                    </FHButton>
                    <Box sx={{ position: 'relative' }}>
                      {target?.dirty && <UnsavedIndicator right="-20px" top="-15px" />}
                      <FHButton
                        data-testid={`targetSaveButton`}
                        colorTheme={Themes.Primary}
                        onClick={handleSaveTarget}
                      >
                        SAVE TARGET
                      </FHButton>
                    </Box>
                  </Grid>
                )}
                {target?.is_active !== activeStatus && (
                  <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', mt: 2 }}>
                    <Typography fontWeight={'bold'} textAlign={'left'} mb={1}>
                      Reason for status change
                    </Typography>
                    <FHInputField
                      data-testid={`targetStatusChangeReasonTextField_${target?.label}`}
                      sx={{ flex: 1 }}
                      inputHeight={40}
                      colorTheme={Themes.Primary}
                      coloredBackground
                      value={reason}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => setReason(e.target.value)}
                      onBlur={() => onUpdateTargetActivation(reason)}
                      error={errors && errors['reason']}
                      helperText={
                        errors && errors['reason']
                          ? `${REASON_FOR_DEACTIVATE_MAX_CHARS} character limit reached`
                          : ''
                      }
                    />
                  </Grid>
                )}
                {!target?.is_editing &&
                  !target?.is_active &&
                  target?.is_active === activeStatus &&
                  target?.reason && (
                    <Grid
                      data-testid={`targetDeactivationReason_${target?.label}_${target?.is_active}`}
                      item
                      xs={12}
                      sx={{ display: 'flex', flexDirection: 'column', mt: 2 }}
                    >
                      <Typography fontWeight={'bold'} textAlign={'left'} mb={1}>
                        Reason for deactivation
                      </Typography>
                      <Typography align="left">{target?.reason || ''}</Typography>
                    </Grid>
                  )}
              </Grid>
            </AccordionDetails>
            <FHConfirmDialog
              open={showConfirmation}
              colorTheme={Themes.Primary}
              title={'Cancel target'}
              content={'Are you sure you want to cancel? Any new information will be lost.'}
              submittext={'Cancel'}
              canceltext={'Return to target'}
              onClose={() => setShowConfirmation(false)}
              onSubmit={handleCancel}
            />
            <FHConfirmDialog
              open={showGoalValidation}
              colorTheme={Themes.Primary}
              title={'Incomplete Goal data'}
              titleStyles={{
                color: FORTA_BLUE.primary,
                fontWeight: 500,
              }}
              dialogStyles={{
                padding: '32px',
                maxHeight: '85vh',
                height: 'auto',
                width: '650px',
                maxWidth: '700px',
              }}
              sx={{
                textAlign: 'left',
                '& .MuiModal-backdrop': {
                  backdropFilter: 'blur(2px)',
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                },
              }}
              contentRenderer={() => (
                <Box sx={{ textAlign: 'left', padding: '20px 0px 20px 0px' }}>
                  <Typography fontSize={18}>
                    Please complete the following required fields in the goal and save them before
                    editing its targets:
                  </Typography>
                  <Box sx={{ mt: 2 }} component="ul">
                    {invalidGoalFields.map((field, index) => (
                      <Typography
                        component="li"
                        key={index}
                        sx={{ fontSize: 18, marginBottom: 0.5 }}
                      >
                        {capitalizeFirstLetter(field)}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
              submittext={'Okay'}
              hideCancelButton
              onClose={() => setShowGoalValidation(false)}
              onSubmit={() => setShowGoalValidation(false)}
            />
          </>
        ) : null}
      </FHAccordion>
    </Box>
  );
}

export default GoalTargetItem;
